import { SECTIONS, type NavItem, type NavSection } from './navigation';

/**
 * The cockpit module of each menu entry (PLAN-GENERICITE § 3.7, phase 7): the navigation hides what a
 * switched-off module serves. A section or an entry not listed belongs to the base, always shown.
 * Kept apart from `navigation.ts` so that the menu itself reads as before.
 *
 * The same menu also follows the member's scope (§ 3.1): an entry that plainly serves one data domain
 * (`data_domains.slug`) is hidden from a member whose non-empty scope leaves that domain out. Comfort,
 * not security (the RLS by scope is the multi-tenant work); an entry serving several domains, or none
 * in particular, is not listed and always shown.
 *
 * And it follows what the person may open (`open`, from `my_access()`: `src/lib/access/screenAccess.ts`, lot L6g of
 * PLAN-MULTI-TENANT): an entry whose screen is not open to them is hidden, a section with none of its screens open too.
 * That one is the rule the route guard applies, not a comfort.
 */
export const SECTION_MODULE: Partial<Record<NavSection, string>> = {
  inbox: 'widget',
  onboarding: 'onboarding',
  newsletter: 'marketing',
};

export const SECTION_DOMAIN: Partial<Record<NavSection, string>> = {
  inbox: 'conversations-widget',
  newsletter: 'newsletter',
  onboarding: 'onboarding',
};

export const ITEM_DOMAIN: Readonly<Record<string, string>> = {
  '/admin/revenue': 'stripe-mrr',
  '/admin/facturation': 'facturation',
  '/admin/crm/opportunities': 'crm',
  '/admin/crm/accounts': 'crm',
  '/admin/crm/carte': 'crm',
  '/admin/crm/contacts': 'crm',
  '/admin/appointments': 'rdv-cr',
  '/admin/kb/articles': 'kb',
  '/admin/linkedin': 'linkedin',
  '/admin/backlog': 'features-roadmap',
  '/admin/feature-requests': 'features-roadmap',
  '/admin/versions': 'features-roadmap',
  '/admin/client-surveys': 'conversations-widget',
  '/admin/qualite': 'qualite-produit',
  '/admin/settings/kb-categories': 'kb',
  '/admin/settings/stripe-products': 'stripe-mrr',
  '/admin/settings/stripe-events': 'stripe-mrr',
  '/admin/settings/appointment-types': 'rdv-cr',
  '/admin/settings/appointment-availability': 'rdv-cr',
  '/admin/settings/appointment-settings': 'rdv-cr',
  '/admin/settings/meeting-reports': 'rdv-cr',
};

/** No restriction: the scope of an owner, of a member without one, and while the record is read. */
export const FULL_SCOPE: readonly string[] = [];

/** Every screen open: the menus of the tests that do not exercise the access. */
export const ALL_OPEN = (): boolean => true;

const inScope = (domain: string | undefined, scope: readonly string[]) =>
  domain === undefined || scope.length === 0 || scope.includes(domain);

export const ITEM_MODULE: Readonly<Record<string, string>> = {
  '/admin/revenue': 'facturation',
  '/admin/facturation': 'facturation',
  '/admin/kb/articles': 'kb',
  '/admin/linkedin': 'marketing',
  '/admin/personas': 'marketing',
  '/admin/product-intelligence': 'product-intelligence',
  '/admin/my-dashboard': 'roadmap',
  '/admin/backlog': 'roadmap',
  '/admin/feature-requests': 'roadmap',
  '/admin/versions': 'roadmap',
  '/admin/roadmap-preview': 'roadmap',
  '/admin/feature-announcements': 'roadmap',
  '/admin/client-announcements': 'widget',
  '/admin/widget-tours': 'widget',
  '/admin/widget-badges': 'widget',
  '/admin/client-surveys': 'widget',
  '/admin/qualite': 'qa',
  '/admin/settings/kb-categories': 'kb',
  '/admin/settings/stripe-products': 'facturation',
  '/admin/settings/stripe-events': 'facturation',
  '/admin/settings/widget-config': 'widget',
};

/** Whether a section is shown: its module is on (or it has none), and its page or one of its entries is open. Pure. */
export function sectionOn(
  id: NavSection,
  off: ReadonlySet<string>,
  scope: readonly string[] = FULL_SCOPE,
  open: (href: string) => boolean = ALL_OPEN,
): boolean {
  const module = SECTION_MODULE[id];
  const section = SECTIONS.find((s) => s.id === id);
  const reachable = section !== undefined && (open(section.href) || section.sidebarItems.some((item) => open(item.href)));
  return (module === undefined || !off.has(module)) && inScope(SECTION_DOMAIN[id], scope) && reachable;
}

/** The entries whose module is on, whose domain is in scope and whose screen is open, in their order; a divider moves
 *  to the next shown entry. Pure. */
export function itemsOn(
  items: readonly NavItem[],
  off: ReadonlySet<string>,
  scope: readonly string[] = FULL_SCOPE,
  open: (href: string) => boolean = ALL_OPEN,
): NavItem[] {
  if (off.size === 0 && scope.length === 0 && open === ALL_OPEN) return [...items];
  const shown: NavItem[] = [];
  let divider = false;
  for (const item of items) {
    const module = ITEM_MODULE[item.href];
    if ((module !== undefined && off.has(module)) || !inScope(ITEM_DOMAIN[item.href], scope) || !open(item.href)) {
      divider ||= item.dividerBefore === true;
      continue;
    }
    shown.push(divider && !item.dividerBefore ? { ...item, dividerBefore: true } : item);
    divider = false;
  }
  return shown;
}
